// @ts-nocheck
import MobileNavigation from './components/MobileNavigation/MobileNavigation';
import DesktopNavigation from './components/DesktopNavigation/DesktopNavigation';
import TripOverview from './components/TripOverview/TripOverview';
import Itinerary from './components/Itinerary/Itinerary';
import Information from './components/Information/Information';
import AskMe from './components/AskMe/AskMe';
import ManifestManager from './ManifestManager';
import TravelerAppSkeleton from './components/TravelerAppSkeleton/TravelerAppSkeleton';
import NotFoundPage from '../../components/NotFoundPage/NotFoundPage';
import globalConfig from '../../config/globalConfig';
import InstallBanner from './components/InstallBanner/InstallBanner';
import InstallModal from './components/InstallModal/InstallModal';
import OfflineIndicator from './components/OfflineIndicator/OfflineIndicator';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getAppManifest, getTripData } from './services/travelerapp';
import {
  getTenantSettings,
  getTripSettings
} from '../../services/tenant/tenant';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { injectPWAMetaTags, cleanupPWAMetaTags } from './helpers/metatag';
import { syncAndCacheManager } from './services/syncAndCacheManager';
import { pwaInstallManager } from './services/pwaInstallManager';
import { useOnlineStatus } from './context/OnlineStatusContext/OnlineStatusContext';
import { useAppDataSocket } from './hooks/useAppDataSocket';
import { useDispatch, useSelector } from 'react-redux';
import { setHeadingFont, setBodyFont } from '../../redux/slices/font/font';
import FontDataConfig from '../../components/GlobalSettings/Templates/FontDataConfig/FontDataConfig';
import {
  parseContactSettings,
  withResolvedOperatorContact
} from '../../utils/operatorContact';
import {
  parseTripLogoSetting,
  withResolvedOperatorLogo
} from '../../utils/operatorLogo';
import './TravelerApp.css';

const SETTINGCODE = 'TEMPLATEFIELDS';
const GLOBAL_CONTACT_SETTINGCODE = 'GLOBALCONTACT';
const TRIP_LOGO_SETTINGCODE = 'TRIPLOGOURL';
syncAndCacheManager.apiUrl = globalConfig.axios.apiUrl;

const hexToRgb = (hex = '') => {
  const normalized = String(hex).replace('#', '').trim();

  if (normalized.length !== 6) return null;

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;

  return { r, g, b };
};

const toRgba = (hex, alpha = 1) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const TravelerApp = () => {
  const location = useLocation();
  const { isOnline } = useOnlineStatus();
  const { tripId } = useParams();
  const dispatch = useDispatch();
  const { enableOperatorContactSettings } = useFlags();
  const [activeTab, setActiveTab] = useState('overview');
  const hasMountedTabScrollRef = useRef(false);
  const [tripData, setTripData] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [templateSettings, setTemplateSettings] = useState(null);
  const operatorContactSettingsRef = useRef({});
  const rawTripDataRef = useRef(null);
  const [tripLogoUrl, setTripLogoUrl] = useState('');
  const tripLogoUrlRef = useRef('');
  const [loading, setLoading] = useState(true);
  const [showInstallModal, setShowInstallModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [cacheStatus, setCacheStatus] = useState('not-cached');
  const [responseError, setResponseError] = useState(null);
  const queryParams = new URLSearchParams(location?.search);
  const tripKey = queryParams?.get('uuid');

  const headingFont = useSelector((state) => state.font.headingFont) || 'Inter';
  const bodyFont = useSelector((state) => state.font.bodyFont) || 'Inter';

  const setCurrentOperatorContactSettings = useCallback((settings = {}) => {
    operatorContactSettingsRef.current = settings;
  }, []);

  const updateTripLogoUrl = useCallback((nextTripLogoUrl = '') => {
    tripLogoUrlRef.current = nextTripLogoUrl;
    setTripLogoUrl(nextTripLogoUrl);
  }, []);

  const fetchOperatorContactSettings = useCallback(async () => {
    if (!enableOperatorContactSettings) {
      return {};
    }

    const [tripContactResult, globalContactResult] = await Promise.allSettled([
      getTripSettings(tripId, SETTINGCODE),
      getTenantSettings(GLOBAL_CONTACT_SETTINGCODE)
    ]);

    return {
      tripContactSettings:
        tripContactResult.status === 'fulfilled'
          ? parseContactSettings(tripContactResult.value?.data?.settingvalue)
          : {},
      globalContactSettings:
        globalContactResult.status === 'fulfilled'
          ? parseContactSettings(globalContactResult.value?.data?.settingvalue)
          : {}
    };
  }, [enableOperatorContactSettings, tripId]);

  const fetchTripLogoUrl = useCallback(async () => {
    if (!enableOperatorContactSettings) {
      return '';
    }

    try {
      const tripLogoSettings = await getTripSettings(
        tripId,
        TRIP_LOGO_SETTINGCODE
      );

      return parseTripLogoSetting(tripLogoSettings?.data?.settingvalue);
    } catch (error) {
      return '';
    }
  }, [enableOperatorContactSettings, tripId]);

  const resolveTripDisplayData = useCallback(
    (
      data,
      {
        contactSettings = operatorContactSettingsRef.current,
        logoUrl = tripLogoUrlRef.current
      } = {}
    ) => {
      const contactResolvedData = withResolvedOperatorContact(data, {
        ...contactSettings,
        enabled: enableOperatorContactSettings
      });
      const pwaContactResolvedData = {
        ...contactResolvedData,
        createdbyuser:
          data?.createdbyuser || contactResolvedData?.createdbyuser || {},
        tenant: {
          ...(contactResolvedData?.tenant || {}),
          tenantphone:
            data?.tenant?.tenantphone ||
            contactResolvedData?.tenant?.tenantphone ||
            ''
        }
      };

      return withResolvedOperatorLogo(pwaContactResolvedData, {
        tripLogoUrl: logoUrl,
        enabled: enableOperatorContactSettings
      });
    },
    [enableOperatorContactSettings]
  );

  const syncTripDataWithResolvedDisplay = useCallback(() => {
    if (enableOperatorContactSettings === undefined) {
      return false;
    }

    return syncAndCacheManager.syncTripData(tripId, tripKey);
  }, [enableOperatorContactSettings, tripId, tripKey]);

  const refreshOperatorContactDisplay = useCallback(async () => {
    if (!enableOperatorContactSettings) {
      return;
    }

    let nextOperatorContactSettings = {};
    try {
      nextOperatorContactSettings = await fetchOperatorContactSettings();
      setCurrentOperatorContactSettings(nextOperatorContactSettings);
    } catch (error) {
      return;
    }

    const sourceTripData =
      rawTripDataRef.current || syncAndCacheManager.getCachedTripData(tripId);

    if (!sourceTripData) {
      return;
    }

    rawTripDataRef.current = sourceTripData;

    const resolvedData = resolveTripDisplayData(sourceTripData, {
      contactSettings: nextOperatorContactSettings,
      logoUrl: tripLogoUrlRef.current
    });

    setTripData(resolvedData);

    const cachedData = syncAndCacheManager.getCachedTripData(tripId);
    if (syncAndCacheManager.hasDataChanged(cachedData, resolvedData)) {
      setCacheStatus('caching');
      syncAndCacheManager.cacheTripData(tripId, resolvedData);
    }
  }, [
    enableOperatorContactSettings,
    fetchOperatorContactSettings,
    resolveTripDisplayData,
    setCurrentOperatorContactSettings,
    tripId
  ]);

  const handleAppDataUpdate = useCallback(
    async (event) => {
      if (event !== 'TRIP_UPDATED') {
        return;
      }

      const [nextOperatorContactSettings, nextTripLogoUrl] = await Promise.all([
        fetchOperatorContactSettings(),
        fetchTripLogoUrl()
      ]);
      setCurrentOperatorContactSettings(nextOperatorContactSettings);
      updateTripLogoUrl(nextTripLogoUrl);

      setTripData((currentTripData) =>
        rawTripDataRef.current
          ? resolveTripDisplayData(rawTripDataRef.current, {
              contactSettings: nextOperatorContactSettings,
              logoUrl: nextTripLogoUrl
            })
          : currentTripData
      );

      // Refresh trip data in background
      syncTripDataWithResolvedDisplay();
    },
    [
      fetchOperatorContactSettings,
      fetchTripLogoUrl,
      resolveTripDisplayData,
      setCurrentOperatorContactSettings,
      syncTripDataWithResolvedDisplay,
      updateTripLogoUrl
    ]
  );

  useEffect(() => {
    if (!hasMountedTabScrollRef.current) {
      hasMountedTabScrollRef.current = true;
      return;
    }

    const mainScroller = document.querySelector('.traveler-app__content');
    if (mainScroller) {
      mainScroller.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }

    if (document.scrollingElement) {
      document.scrollingElement.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  }, [activeTab]);

  useEffect(() => {
    if (enableOperatorContactSettings === undefined) return;

    syncAndCacheManager.setTripDataTransform(tripId, (data) =>
      resolveTripDisplayData(data)
    );

    return () => {
      syncAndCacheManager.setTripDataTransform(tripId);
    };
  }, [
    enableOperatorContactSettings,
    resolveTripDisplayData,
    tripId,
    tripLogoUrl
  ]);

  // WebSocket listener for trip events
  useAppDataSocket(tripId, handleAppDataUpdate);

  useEffect(() => {
    if (!enableOperatorContactSettings) return;

    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshOperatorContactDisplay();
      }
    };

    window.addEventListener('focus', refreshOperatorContactDisplay);
    window.addEventListener('online', refreshOperatorContactDisplay);
    document.addEventListener('visibilitychange', refreshOnVisible);

    const intervalId = window.setInterval(refreshOperatorContactDisplay, 60000);

    return () => {
      window.removeEventListener('focus', refreshOperatorContactDisplay);
      window.removeEventListener('online', refreshOperatorContactDisplay);
      document.removeEventListener('visibilitychange', refreshOnVisible);
      window.clearInterval(intervalId);
    };
  }, [enableOperatorContactSettings, refreshOperatorContactDisplay]);

  useEffect(() => {
    if (enableOperatorContactSettings === undefined) return;

    // Fetch app manifest
    const fetchManifest = async () => {
      try {
        const manifest = await getAppManifest(tripId);
        setManifest(manifest);
      } catch (error) {
        if (error?.code != 'ERR_NETWORK') {
          setResponseError(error);
        }
      }
    };

    // Fetch trip data
    const fetchTripData = async () => {
      try {
        const [nextOperatorContactSettings, nextTripLogoUrl] =
          await Promise.all([
            fetchOperatorContactSettings(),
            fetchTripLogoUrl()
          ]);
        setCurrentOperatorContactSettings(nextOperatorContactSettings);
        updateTripLogoUrl(nextTripLogoUrl);

        const { data, error } = await getTripData(tripId, tripKey);
        if (data && !error) {
          rawTripDataRef.current = data;
          const resolvedData = resolveTripDisplayData(data, {
            contactSettings: nextOperatorContactSettings,
            logoUrl: nextTripLogoUrl
          });
          setTripData(resolvedData);
          setLoading(false);
          setResponseError(null);

          // Cache the trip data in background
          setCacheStatus('caching');
          syncAndCacheManager.cacheTripData(tripId, resolvedData);

          // If online, sync in background
          if (isOnline && syncAndCacheManager.isTripCached(tripId)) {
            syncTripDataWithResolvedDisplay();
          }
        } else {
          setResponseError(error);
        }
      } catch (error) {
        // Online with non-network error
        if (error?.code != 'ERR_NETWORK') {
          setResponseError(error);
        } else {
          // Fallback to cached data if offline
          if (!isOnline) {
            const cachedData = syncAndCacheManager.getCachedTripData(tripId);

            if (cachedData) {
              console.log('[TravelerApp] Using cached data (offline)');
              rawTripDataRef.current = cachedData;
              setTripData(resolveTripDisplayData(cachedData));
              setCacheStatus('cached');
              setResponseError(null);
            } else {
              setResponseError('No cached data available');
            }
          }
        }

        setLoading(false);
      }
    };

    // Fetch template settings
    const fetchTemplateSettings = async () => {
      try {
        const settingsData = await getTenantSettings(SETTINGCODE);
        if (!settingsData?.data || !settingsData?.data?.settingvalue) {
          setTemplateSettings({ color: '#0D69D4' });
          dispatch(setHeadingFont('Inter'));
          dispatch(setBodyFont('Inter'));
        }
        if (settingsData?.data?.settingvalue) {
          const settingvalue = JSON.parse(settingsData.data.settingvalue)[0];
          setTemplateSettings(settingvalue);

          const { headingfont, bodyfont } = settingvalue || {};
          dispatch(setHeadingFont(headingfont ?? 'Inter'));
          dispatch(setBodyFont(bodyfont ?? 'Inter'));
        }
      } catch (error) {
        setTemplateSettings({ color: '#0D69D4' });
        dispatch(setHeadingFont('Inter'));
        dispatch(setBodyFont('Inter'));
      }
    };

    fetchManifest();
    fetchTemplateSettings();
    fetchTripData();
  }, [
    dispatch,
    enableOperatorContactSettings,
    fetchOperatorContactSettings,
    fetchTripLogoUrl,
    isOnline,
    resolveTripDisplayData,
    setCurrentOperatorContactSettings,
    syncTripDataWithResolvedDisplay,
    tripId,
    tripKey,
    updateTripLogoUrl
  ]);

  // Inject PWA meta tags when manifest is available
  useEffect(() => {
    if (manifest) {
      injectPWAMetaTags(manifest);
    }

    // Cleanup on unmount
    return () => {
      cleanupPWAMetaTags();
    };
  }, [manifest]);

  // Initialize offline manager
  useEffect(() => {
    syncAndCacheManager.initServiceWorker();
  }, [tripId]);

  // Listen for cache events
  useEffect(() => {
    const handleCacheComplete = (event) => {
      if (event.detail.tripId === tripId) {
        setCacheStatus('cached');
        console.log('[TravelerApp] Trip cached successfully');
      }
    };

    const handleCacheError = (event) => {
      if (event.detail.tripId === tripId) {
        setCacheStatus('error');
        console.error('[TravelerApp] Cache error:', event.detail.error);
      }
    };

    const handleDataUpdated = (event) => {
      if (event.detail.tripId === tripId) {
        rawTripDataRef.current = event.detail.rawData || event.detail.data;
        setTripData(resolveTripDisplayData(rawTripDataRef.current));
        console.log('[TravelerApp] Trip data updated from sync');
      }
    };

    window.addEventListener('tripCacheComplete', handleCacheComplete);
    window.addEventListener('tripCacheError', handleCacheError);
    window.addEventListener('tripDataUpdated', handleDataUpdated);

    return () => {
      window.removeEventListener('tripCacheComplete', handleCacheComplete);
      window.removeEventListener('tripCacheError', handleCacheError);
      window.removeEventListener('tripDataUpdated', handleDataUpdated);
    };
  }, [resolveTripDisplayData, tripId]);

  // Handle install banner click
  const handleInstallClick = async () => {
    if (
      pwaInstallManager.supportsNativeInstall() &&
      pwaInstallManager.isInstallable
    ) {
      // Trigger native install prompt
      const result = await pwaInstallManager.triggerNativeInstall();
      console.log('[PWA] Install result:', result);
    } else if (pwaInstallManager.supportsManualInstall()) {
      // Show manual instructions modal
      setShowInstallModal(true);
    } else {
      // Platform doesn't support installation
      console.log('[PWA] Installation not supported on this platform');
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <TripOverview id='pwa-trip-overview' tripData={tripData} />;
      case 'itinerary':
        return (
          <Itinerary
            id='pwa-itinerary'
            tripData={tripData}
            templateSettings={templateSettings}
          />
        );
      case 'info':
        return (
          <Information
            id='pwa-information'
            info={tripData?.info}
            tripData={tripData}
          />
        );
      case 'askme':
        return <AskMe id='pwa-askme' documents={tripData?.askme} />;
      default:
        return null;
    }
  };

  if (loading && isOnline) {
    return <TravelerAppSkeleton />;
  }

  if (responseError) {
    return (
      <div className='w-100 flex-center'>
        <NotFoundPage id='pwa-not-found' />
      </div>
    );
  }

  const themeColor = templateSettings?.color || '#0D69D4';
  const themeColorMuted =
    toRgba(themeColor, 0.15) || 'rgba(13, 105, 212, 0.15)';
  const themeColorShadow = toRgba(themeColor, 0.3) || 'rgba(13, 105, 212, 0.3)';

  return (
    <FontDataConfig
      fonts={[
        { family: headingFont, weights: [400, 700] },
        { family: bodyFont, weights: [400, 700] }
      ]}
    >
      {() => (
        <div
          className='traveler-app'
          style={{
            '--pwa-heading-font': headingFont,
            '--pwa-body-font': bodyFont,
            '--pwa-theme-color': themeColor,
            '--pwa-theme-color-muted': themeColorMuted,
            '--pwa-theme-color-shadow': themeColorShadow
          }}
        >
          <ManifestManager tripId={tripId} />

          <DesktopNavigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tripData={tripData}
          />

          <main className='traveler-app__content'>
            <OfflineIndicator id='pwa-offline-indicator' />
            {renderContent()}
          </main>

          <InstallBanner
            onInstallClick={handleInstallClick}
            onDismiss={() => console.log('[PWA] Install banner dismissed')}
          />

          <InstallModal
            isOpen={showInstallModal}
            onClose={() => setShowInstallModal(false)}
          />

          <MobileNavigation activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
      )}
    </FontDataConfig>
  );
};

export default TravelerApp;
